import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ORIGIN_LABEL, casePath, matchesSearch, type JobOrder, caseFinished } from '@/domain';
import { useJobOrders, useReleaseHold } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { useTableOrder } from '@/application/usecases/useTableOrder';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { PAGE_SIZES, useTablePage } from '@/application/usecases/useTablePage';
import { strings } from '@/shared/strings';
import { formatDay } from '@/shared/format/instant';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { CaseTable } from '@/ui/data/CaseTable';
import { FilterBar, type FilterDef } from '@/ui/data/FilterBar';
import { SortSelect } from '@/ui/data/SortSelect';
import { casePresets } from '@/ui/data/casePresets';
import { DeliveryDate } from '@/ui/data/DeliveryDate';
import { HoldNote } from '@/ui/data/StatusDot';
import type { Column } from '@/ui/data/ResponsiveTable';
import { orderColumns, seesCaseValue } from '@/ui/data/orderColumns';
import { SkeletonRows } from '@/ui/feedback/Loading';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import screen from '../orders/OrdersScreen.module.css';
import styles from './PaymentsScreen.module.css';

/**
 * On Hold Cases. Section 5 of the request of 16 September 2026: "where do hold
 * cases go?"
 *
 * Nowhere, was the answer. A held case was a group on Payments, a group on
 * Shipping and a row on Orders with a marker, and nobody had a list of them.
 * That matters more than it sounds: a hold is the lab deciding to stop work on
 * a case, and a decision nobody can see the whole of is a decision that gets
 * forgotten about until a dentist telephones.
 *
 * So: one list, the reason on every row, and how long it has been stopped,
 * which is the figure somebody chases on. Releasing is done from here too,
 * because a list of stopped cases that cannot start them again is half a
 * screen.
 *
 * The cases themselves are fetched with `state=held` from the server, so the
 * count under the rows is every held case and not every case on this page.
 *
 * Release is asked about first, in the shared confirmation, with the sentence
 * Payments and Shipping ask it in: the request of 5 October 2026 (third),
 * section 2.
 */
export function OnHoldScreen() {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const release = useReleaseHold();
  const ask = useConfirmAction();

  const [query, setQuery] = useState('');

  const paging = useTablePage('onhold');
  // The columns as this reader laid them out, kept on their account. The request of 7 October 2026, section 3.
  const layout = useTableLayout('onhold');
  const { data, isPending, isError } = useJobOrders(
    useMemo(
      () => ({
        state: 'held',
        includeDispatched: true,
        page: paging.page,
        pageSize: paging.pageSize,
      }),
      [paging.page, paging.pageSize],
    ),
  );

  const presets = useMemo(
    () => casePresets<JobOrder>(['newestFirst', 'dueSoonest', 'urgentFirst', 'doctorAtoZ']),
    [],
  );
  const order = useTableOrder('onhold', presets);

  /*
   * Whoever can take a hold off. The two permissions that put one on are the
   * two that lift it, and the server decides either way: a button nobody can
   * press is worse than no button.
   */
  const mayRelease =
    session?.permissions.includes('pay') === true || session?.permissions.includes('ship') === true;

  const visible = useMemo(
    () =>
      (data?.items ?? []).filter((row) =>
        matchesSearch(query, [
          row.jobOrderNo,
          row.dentistName,
          row.patientName,
          ORIGIN_LABEL[row.origin],
          row.hold.reason,
        ]),
      ),
    [data, query],
  );

  const { reset } = paging;
  useEffect(() => {
    reset();
  }, [reset, query, order.presetKey, order.column]);

  const filters: FilterDef[] = [
    {
      kind: 'search',
      label: strings.onHold.search,
      placeholder: strings.onHold.searchPlaceholder,
      value: query,
      onChange: setQuery,
    },
  ];

  const columns: Column<JobOrder>[] = [
    {
      key: 'reason',
      label: strings.onHold.reason,
      render: (row) => <HoldNote>{row.hold.reason ?? strings.payments.onHold}</HoldNote>,
    },
    {
      key: 'heldAt',
      label: strings.onHold.since,
      mono: true,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.hold.heldAt ?? '',
      render: (row) => (row.hold.heldAt === null ? '' : formatDay(row.hold.heldAt)),
    },
    {
      key: 'deliveryDate',
      label: strings.orders.deliveryDate,
      nowrap: true,
      sortable: true,
      sortValue: (row) => row.deliveryDate,
      render: (row) => <DeliveryDate date={row.deliveryDate} finished={caseFinished(row)} />,
    },
    ...(mayRelease
      ? ([
          {
            key: 'actions',
            label: strings.onHold.actions,
            render: (row) => (
              <div className={styles.actions}>
                <Button
                  disabled={release.isPending}
                  onClick={() =>
                    ask(
                      strings.confirm.releaseHold(row.jobOrderNo),
                      () =>
                        release.mutateAsync(row.jobOrderNo).then(() => {
                          toast(`${row.jobOrderNo} ${strings.payments.holdReleased}`);
                        }),
                      { confirmLabel: strings.payments.releaseHold },
                    )
                  }
                >
                  {strings.payments.releaseHold}
                </Button>
              </div>
            ),
          },
        ] as Column<JobOrder>[])
      : []),
    // What the rows carry and the table does not show as it comes. The request of 8 October 2026 (second).
    ...orderColumns([
      'createdAt',
      'caseType',
      'product',
      'origin',
      'currentStage',
      'clinic',
      ...(seesCaseValue(session?.permissions) ? (['value'] as const) : []),
    ]),
  ];

  return (
    <div>
      <div className={screen.head}>
        <span className={screen.lbl}>{strings.onHold.label}</span>
        <h2>{strings.onHold.title}</h2>
        <span className={screen.hint}>{strings.onHold.hint}</span>
      </div>

      <FilterBar
        label={strings.onHold.filtersLabel}
        filters={filters}
        sort={<SortSelect presets={presets} value={order.presetKey} onChange={order.choose} />}
      />

      {isPending && <SkeletonRows rows={4} label={strings.common.loadingOrders} />}
      {isError && <p className={screen.hint}>{strings.errors.server}</p>}

      {!isPending && !isError && (
        <CaseTable
          customize={layout}
          columns={columns}
          rows={order.apply(visible)}
          sort={order.column}
          onSortChange={order.sortByColumn}
          identity={(row) => ({
            jobOrderNo: row.jobOrderNo,
            urgent: row.urgent,
            // The mark of a remake. The request of 6 October 2026 (second), item 2.
            redo: row.redoOf !== null,
            held: row.hold.held,
            dentistName: row.dentistName,
            patientName: row.patientName,
          })}
          rowKey={(row) => row.jobOrderNo}
          onRowClick={(row) => {
            // Accounts hold the archive's record screen, not Orders': the door
            // that exists for them. Section 13 of the review of 2 October 2026.
            const path = casePath(session?.views ?? [], { jobOrderNo: row.jobOrderNo }, 'record');
            if (path !== null) void navigate(path);
          }}
          caption={strings.onHold.caption}
          emptyHeading={
            query.trim() === '' ? strings.onHold.emptyHeading : strings.table.noMatch
          }
          emptyDetail={
            query.trim() === '' ? strings.onHold.emptyDetail : strings.table.noMatchDetail
          }
          paging={{
            page: paging.page,
            pageSize: paging.pageSize,
            total: data?.total ?? 0,
            sizes: PAGE_SIZES,
            onPage: paging.goTo,
            onPageSize: paging.choose,
            label: strings.table.pagesLabel,
          }}
        />
      )}
    </div>
  );
}
